import { cn } from './cn';
import * as React from 'react';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { filterDateChars, isValidDatePrefix } from './inputDateValidation';

// Input is bottom-of-stack — there's no semantic primitive below to
// compose. Geometry (border, padding, height, focus, disabled) lives
// inline because the chrome IS the primitive. TextArea and Select bake
// the same shape independently; if they ever drift, that's visible
// here in code review, not hidden behind a shared module.
//
// `wm-field` and the CSS variables are read by Form.jsx for label /
// fieldset offsets — keep them on the rendered element.
//
// `type="number"`: defaults `step="any"` so the field accepts floats
// (ratios, base temperatures, etc.) without callers reaching for the
// HTML5 `step` attribute. Pass `step` explicitly to override (e.g.
// `step={1}` for integer-only or `step={0.5}` for a half-unit grid).
//
// `type="date"`: NOT the native `<input type=date>` (whose display
// follows the browser/OS locale — dd/mm/yyyy in PT/ES, mm/dd/yyyy in
// US English — breaking visual consistency with the rest of the app).
// Instead it renders a text field with positional validation: every
// state the input passes through must be a valid PREFIX of a
// yyyy-mm-dd date. Each character is checked against a rule for its
// slot (year starts 1 or 2; month 1st digit is 0 or 1; day 1st digit
// 0/1/2/3; etc.). Applies to single keystrokes AND multi-char paste,
// so:
//   - typing "0" first → rejected (year can't start with 0).
//   - paste "2024-05-15" → accepted (every char obeys its slot).
//   - paste "2014/01/01" → digits-only filter strips slashes →
//     "20140101" → fails at slot 4 (must be '-') → rejected. Input
//     keeps its previous state and the placeholder stays visible.
//   - paste "2024-13-50" → fails at month 2nd digit (1x must be 0-2)
//     → rejected.
//   - paste "2024-02-30" → passes positional rules (Feb 30 looks slot-
//     valid even though it isn't a real day). Caught downstream by
//     `new Date(value)` when the filter strategy uses it.
// A decorative calendar icon sits on the left and the search-style
// inline clear button is available (opt-out via `clearable={false}`).
const WM_FIELD_BASE_CLASSNAME = 'wm-field [--wm-field-border-width:1px] [--wm-field-margin:0px] [--wm-field-padding-x:0.75rem] [--wm-field-padding-y:0.5rem] tw-m-0 tw-block tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-ring-offset-background tw-transition-colors tw-cursor-text disabled:tw-cursor-not-allowed disabled:tw-opacity-45';

// Two stops — `medium` is the default (40px, dense forms and pages),
// `small` (32px) for inline contexts like sidebars or filter bars.
// Canonical control-height scale: small=32, medium=40 (default),
// large=44 — shared with Button / IconButton / Select / TextArea.
const WM_FIELD_SIZE_CLASSNAMES = {
    small: 'tw-h-8 tw-px-2.5 tw-py-1 tw-text-xs tw-leading-5',
    medium: 'tw-h-10 tw-px-3 tw-py-2 tw-text-sm tw-leading-6',
    large: 'tw-h-11 tw-px-3.5 tw-py-2.5 tw-text-sm tw-leading-6'
};

const WM_FIELD_FOCUS_CLASSNAME = 'focus:tw-border-muted-foreground focus:tw-outline-none focus:tw-ring-0 focus:tw-ring-offset-0 focus-visible:tw-border-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0';

// Per-variant chrome. Each entry pulls together everything that
// distinguishes search / date / password from a plain text field —
// the leading icon (if any), the wm-field BEM modifier class read by
// `ui_reset.css` to keep icon padding through focus, and a label for
// the inline clear button (when relevant). Plain text falls through
// the default branch in the resolvers below.
const VARIANT_CHROME = {
    search: { leadingIcon: 'magnifyingGlass', modifierClass: 'wm-field--search' },
    date: { leadingIcon: 'calendar', modifierClass: 'wm-field--date' },
    password: { leadingIcon: null, modifierClass: 'wm-field--password hide-password-toggle' }
};

const Input = React.forwardRef(({ className: _className, style: _style, clearable = true, defaultValue, defaultVisible = false, disabled, onChange, placeholder, size = 'medium', step, type, value, ...props }, ref) => {
    const resolvedSize = WM_FIELD_SIZE_CLASSNAMES[size] ? size : 'medium';
    const inputRef = React.useRef(null);
    const isControlled = value !== undefined;
    const isDate = type === 'date';
    const isNumber = type === 'number';
    const isPassword = type === 'password';
    const isSearch = type === 'search';
    const variantChrome = VARIANT_CHROME[type] ?? null;
    // Search and date both render a leading icon + an inline clear; the
    // wrapper paths in the JSX below are unified through these flags so
    // we don't keep two near-identical branches in sync.
    const hasLeadingIcon = Boolean(variantChrome?.leadingIcon);
    const hasInlineClear = hasLeadingIcon && clearable;
    const {
        autoComplete,
        autoCorrect,
        enterKeyHint,
        inputMode,
        role,
        spellCheck,
        ...restProps
    } = props;
    const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
    const [showPassword, setShowPassword] = React.useState(isPassword && defaultVisible);

    React.useImperativeHandle(ref, () => inputRef.current);

    const currentValue = isControlled ? (value ?? '') : internalValue;
    const hasValue = String(currentValue ?? '').length > 0;
    // Defaults for the variants that ship a placeholder of their own.
    // Caller-provided `placeholder` always wins.
    const VARIANT_PLACEHOLDERS = {
        search: () => i18next.t('glossary.search').capitalize(),
        date: () => 'yyyy-mm-dd'
    };
    const resolvedPlaceholder = placeholder ?? VARIANT_PLACEHOLDERS[type]?.() ?? undefined;
    // Variants that render as a `<input type=text>` under the hood, even
    // though their public `type` is something else (password swaps
    // dynamically with the visibility toggle, search/date are always
    // text). Anything not in this map falls through to the consumer's
    // raw `type`.
    const VARIANT_NATIVE_TYPE = {
        search: 'text',
        date: 'text',
        password: showPassword ? 'text' : 'password'
    };
    const resolvedType = VARIANT_NATIVE_TYPE[type] ?? type;
    // `type="number"` HTML inputs default to `step="1"`, which means
    // the browser rejects decimals (validation fails on submit, and the
    // up/down spinner only steps integers). Almost every real number
    // field in the app is a float (ratios, base temperatures, etc.),
    // so we default `step="any"` here — callers can still pass `step`
    // explicitly to lock to integers or a specific grid.
    const resolvedStep = isNumber ? (step ?? 'any') : step;

    const handleChange = (event) => {
        let nextValue = event.target.value;

        // Date variant: strip non-digit / non-dash chars first (paste
        // safety), then apply positional validation — but only on
        // additive or same-length changes. Length-decreasing changes
        // (backspace mid-string, cut, delete) are always permissive,
        // so the user can do the natural "delete a char, retype it"
        // flow without the input rejecting the intermediate state.
        //
        // Example: editing `2025-01-01` to `2024-01-01`.
        //   1. User deletes `5` at position 3 → `202-01-01`. Length
        //      dropped (10 → 9) — skip positional check, accept the
        //      intermediate even though slot 3 now holds `-`.
        //   2. User types `4` at position 3 → `2024-01-01`. Length
        //      grew back (9 → 10) — positional check on full value,
        //      every slot valid, accept.
        //
        // Rejected branch: when validation fails, no internal state
        // update, no upstream onChange, no DOM mutation. For controlled
        // inputs the parent's `value` prop never changed, so React
        // re-renders back to the old value on its own; for uncontrolled
        // we force the DOM input.value back to the previous value
        // ourselves.
        if (isDate) {
            const filtered = filterDateChars(nextValue);
            const isAdditive = filtered.length >= currentValue.length;

            if (isAdditive && !isValidDatePrefix(filtered)) {
                // Cursor restore: when we reject a keystroke / paste, the
                // browser has already advanced the cursor past the chars
                // the user inserted, and any DOM revert (ours, or React's
                // controlled-input reconciliation) defaults the cursor to
                // the end. We capture the "before" position by subtracting
                // the number of chars the user added (which we know from
                // event.target.value.length vs currentValue.length) and
                // restore it after the next paint, so the user sees their
                // cursor stay where they were typing.
                const input = inputRef.current;
                const cursorAfter = input?.selectionStart ?? 0;
                const charsAdded = event.target.value.length - currentValue.length;
                const cursorBefore = Math.max(0, cursorAfter - charsAdded);

                if (!isControlled && input) {
                    input.value = currentValue;
                }

                if (input) {
                    requestAnimationFrame(() => {
                        input.setSelectionRange(cursorBefore, cursorBefore);
                    });
                }

                return;
            }

            nextValue = filtered;

            if (nextValue !== event.target.value) {
                event.target.value = nextValue;
            }
        }

        if (!isControlled) {
            setInternalValue(nextValue);
        }

        onChange?.(event);
    };

    const clearValue = () => {
        if (disabled || !inputRef.current) {
            return;
        }

        const inputElement = inputRef.current;
        const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

        if (nativeValueSetter) {
            nativeValueSetter.call(inputElement, '');
        } else {
            inputElement.value = '';
        }

        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        inputElement.focus();
    };

    const inputElement = (
        <input
            autoComplete={isSearch ? 'off' : autoComplete}
            autoCorrect={isSearch ? 'off' : autoCorrect}
            enterKeyHint={isSearch ? 'search' : enterKeyHint}
            type={resolvedType}
            className={cn(
                WM_FIELD_BASE_CLASSNAME,
                WM_FIELD_SIZE_CLASSNAMES[resolvedSize],
                WM_FIELD_FOCUS_CLASSNAME,
                'file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium file:tw-text-foreground placeholder:tw-text-muted-foreground',
                // `pl-8` always reserves space for the leading icon
                // (magnifying glass for search, calendar for date);
                // `pr-8` only when the inline clear button is present,
                // otherwise the input gains back natural right padding.
                hasLeadingIcon && `${variantChrome.modifierClass} tw-pl-8${hasInlineClear ? ' tw-pr-8' : ''}`,
                isPassword && `${variantChrome.modifierClass} tw-pr-8`
            )}
            defaultValue={defaultValue}
            disabled={disabled}
            inputMode={isSearch ? 'search' : inputMode}
            onChange={handleChange}
            placeholder={resolvedPlaceholder}
            ref={inputRef}
            role={isSearch ? 'searchbox' : role}
            spellCheck={isSearch ? false : spellCheck}
            step={resolvedStep}
            value={value}
            {...restProps}
        />
    );

    if (!hasLeadingIcon && !isPassword) {
        return inputElement;
    }

    return (
        <div className="tw-relative tw-w-full">
            {hasLeadingIcon ? (
                <span className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center">
                    <Icon icon={variantChrome.leadingIcon} size="small" tone="muted" />
                </span>
            ) : null}
            {inputElement}
            {/* Inline clear is opt-out (`clearable={false}`) for variants
                hosted inside a container that already owns its own
                "clear" affordance — e.g. the DataGrid filter popover —
                so the user doesn't see two redundant clear buttons. */}
            {hasInlineClear ? (
                <span className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center">
                    <IconButton
                        aria-label={i18next.t('common.clear')}
                        disabled={!hasValue || disabled}
                        icon="close"
                        onClick={clearValue}
                        onMouseDown={event => event.preventDefault()}
                        size="tiny"
                    />
                </span>
            ) : null}
            {isPassword ? (
                <span className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center">
                    <IconButton
                        aria-label={showPassword ? i18next.t('common.hide') : i18next.t('common.show')}
                        aria-pressed={showPassword}
                        disabled={!hasValue || disabled}
                        icon={showPassword ? 'eye' : 'eyeSlash'}
                        onClick={() => setShowPassword(prev => !prev)}
                        onMouseDown={event => event.preventDefault()}
                        size="tiny"
                    />
                </span>
            ) : null}
            {isPassword ? (
                <style>{`
                    .hide-password-toggle::-ms-reveal,
                    .hide-password-toggle::-ms-clear {
                        visibility: hidden;
                        pointer-events: none;
                        display: none;
                    }
                `}</style>
            ) : null}
        </div>
    );
});
Input.displayName = 'Input';

export { Input };
